import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Globe, Mail, Building2, Target, X, Video, Link2, UserPlus, Loader2, Plus } from "lucide-react";
import { TIMEZONES } from "../../lib/timezones";

const tzAbbr = (tz, date = new Date()) => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" }).formatToParts(date);
    return parts.find((p) => p.type === "timeZoneName")?.value || tz;
  } catch { return tz; }
};

const dayKeyInTz = (iso, tz) => {
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));
  } catch { return null; }
};

const timeInTz = (iso, tz) => {
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit" }).format(new Date(iso));
  } catch { return ""; }
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DEFAULT_TZ = "America/New_York"; // display fallback for bookings with no saved zone

export default function BookingsCalendar({ bookings, tz, onTzChange, onSetAttendance, onSetTz, myTz, onEnsureMeet, onAddParticipant, onRemoveParticipant }) {
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const [selectedId, setSelectedId] = useState(null);
  const [showMyTz, setShowMyTz] = useState(false);
  const [genning, setGenning] = useState(false);
  const [partEmail, setPartEmail] = useState("");
  const [adding, setAdding] = useState(false);

  // Each meeting is shown in the timezone the CLIENT booked in (b.tz), unless the
  // "my time zone" toggle is on (then everything converts to the admin's zone).
  // Untracked older bookings fall back to Eastern — there is NO global zone
  // control, so changing one meeting's zone never affects any other meeting.
  const bookingTz = (b) => (showMyTz && myTz ? myTz : (b.tz || DEFAULT_TZ));

  const byDay = useMemo(() => {
    const map = {};
    for (const b of bookings || []) {
      if (!b.slot_iso) continue;
      const key = dayKeyInTz(b.slot_iso, bookingTz(b));
      if (!key) continue;
      (map[key] = map[key] || []).push(b);
    }
    for (const key of Object.keys(map)) {
      map[key].sort((a, c) => new Date(a.slot_iso) - new Date(c.slot_iso));
    }
    return map;
  }, [bookings, showMyTz, myTz]);

  const cells = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1);
    const offset = first.getDay();
    const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < offset; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      arr.push(`${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
    }
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [cursor]);

  const todayKey = dayKeyInTz(new Date().toISOString(), DEFAULT_TZ);
  const monthLabel = new Date(cursor.y, cursor.m, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const selected = selectedId ? (bookings || []).find((b) => b.id === selectedId) : null;

  const move = (delta) => {
    setCursor((c) => {
      const d = new Date(c.y, c.m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  };

  const pillClass = (b) => {
    if (b.attendance === "showed") return "bg-[#6b7c3a]/14 text-[#4d5a24] border-[#6b7c3a]/40";
    if (b.attendance === "no_show") return "bg-[#b23a48]/10 text-[#b23a48] border-[#b23a48]/35 line-through";
    return "bg-[#1e3d3a]/6 text-[#1e3d3a] border-[#1e3d3a]/15 hover:border-[#c9a961]";
  };

  return (
    <div data-testid="bookings-calendar" className="rounded-2xl border border-[#1e3d3a]/12 bg-white overflow-hidden">
      <div className="flex items-center justify-between gap-3 flex-wrap px-5 py-4 border-b border-[#1e3d3a]/10 bg-[#eae7de]/60">
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="calendar-prev-month"
            onClick={() => move(-1)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#1e3d3a]/15 text-[#1e3d3a]/60 hover:text-[#1e3d3a] hover:border-[#c9a961] transition-colors duration-300"
          >
            <ChevronLeft className="h-4 w-4" strokeWidth={1.8} />
          </button>
          <span data-testid="calendar-month-label" className="text-sm font-semibold text-[#1e3d3a] min-w-[150px] text-center">{monthLabel}</span>
          <button
            type="button"
            data-testid="calendar-next-month"
            onClick={() => move(1)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#1e3d3a]/15 text-[#1e3d3a]/60 hover:text-[#1e3d3a] hover:border-[#c9a961] transition-colors duration-300"
          >
            <ChevronRight className="h-4 w-4" strokeWidth={1.8} />
          </button>
          <button
            type="button"
            data-testid="calendar-today-btn"
            onClick={() => { const d = new Date(); setCursor({ y: d.getFullYear(), m: d.getMonth() }); }}
            className="ml-1 rounded-full border border-[#1e3d3a]/15 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/60 hover:text-[#1e3d3a] hover:border-[#c9a961] transition-colors duration-300"
          >
            Today
          </button>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {myTz && (
            <button
              type="button"
              data-testid="calendar-mytz-toggle"
              onClick={() => setShowMyTz((v) => !v)}
              title={showMyTz ? "Showing every meeting in YOUR time zone" : "Show every meeting converted to your time zone"}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] transition-colors duration-300 ${
                showMyTz
                  ? "bg-[#1e3d3a] text-white border-[#1e3d3a]"
                  : "border-[#1e3d3a]/15 text-[#1e3d3a]/60 hover:text-[#1e3d3a] hover:border-[#c9a961]"
              }`}
            >
              <Globe className="h-3.5 w-3.5" strokeWidth={1.7} />
              {showMyTz ? `My zone · ${tzAbbr(myTz)}` : "View in my zone"}
            </button>
          )}
          <span className="text-[10px] text-[#1e3d3a]/40 hidden md:inline">
            Each meeting shows in the client&rsquo;s own zone — set it in the meeting details.
          </span>
        </div>
      </div>

      <div className="grid grid-cols-7 border-b border-[#1e3d3a]/8 bg-[#eae7de]/40">
        {WEEKDAYS.map((d) => (
          <div key={d} className="px-2 py-2 text-center text-[9px] uppercase tracking-[0.25em] text-[#1e3d3a]/50">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {cells.map((key, i) => {
          const dayBookings = key ? (byDay[key] || []) : [];
          const isToday = key === todayKey;
          return (
            <div
              key={key || `pad-${i}`}
              data-testid={key ? `calendar-day-${key}` : undefined}
              className={`min-h-[92px] border-b border-r border-[#1e3d3a]/6 p-1.5 ${key ? "" : "bg-[#eae7de]/25"} ${isToday ? "bg-[#c9a961]/8" : ""}`}
            >
              {key && (
                <div className={`text-[10px] tabular-nums mb-1 ${isToday ? "inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#1e3d3a] text-white font-semibold" : "text-[#1e3d3a]/45"}`}>
                  {parseInt(key.slice(-2), 10)}
                </div>
              )}
              <div className="space-y-1">
                {dayBookings.map((b) => {
                  const bt = bookingTz(b);
                  const bAbbr = tzAbbr(bt, new Date(b.slot_iso));
                  return (
                  <button
                    key={b.id}
                    type="button"
                    data-testid={`calendar-booking-${b.id}`}
                    onClick={() => setSelectedId((cur) => (cur === b.id ? null : b.id))}
                    title={`${b.name} — ${timeInTz(b.slot_iso, bt)} ${bAbbr} (${bt})`}
                    className={`w-full text-left rounded-md border px-1.5 py-1 text-[10px] leading-tight transition-colors duration-200 ${pillClass(b)} ${selectedId === b.id ? "ring-2 ring-[#c9a961]" : ""}`}
                  >
                    <span className="font-semibold tabular-nums">{timeInTz(b.slot_iso, bt)}</span>
                    <span className="ml-1 text-[8px] uppercase tracking-wide opacity-70">{bAbbr}</span>
                    <span className="block truncate">{b.name}</span>
                  </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {selected && (
        <div data-testid="calendar-booking-detail" className="border-t border-[#c9a961]/40 bg-[#c9a961]/6 px-5 py-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-[#1e3d3a]">{selected.name}</span>
                {selected.advisor === "chris" && (
                  <span className="text-[9px] uppercase tracking-[0.2em] bg-[#c9a961]/25 text-[#a3823f] border border-[#c9a961]/50 px-1.5 py-0.5 rounded-full">Chris · Advisor</span>
                )}
              </div>
              <div data-testid="calendar-detail-time" className="text-xs text-[#a3823f] font-semibold uppercase tracking-[0.15em]">
                {new Intl.DateTimeFormat(undefined, { timeZone: bookingTz(selected), weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(selected.slot_iso))} {tzAbbr(bookingTz(selected), new Date(selected.slot_iso))}
              </div>
              <div data-testid="calendar-detail-clienttz" className="flex items-center gap-2 flex-wrap text-[10px] text-[#1e3d3a]/60">
                <Globe className="h-3 w-3 text-[#a3823f]" strokeWidth={1.7} />
                <span>Client&rsquo;s time zone:</span>
                {onSetTz ? (
                  <select
                    data-testid="calendar-detail-tz-select"
                    value={selected.tz || DEFAULT_TZ}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => onSetTz(selected, e.target.value)}
                    title="Set this client's time zone (only affects this meeting)"
                    className="rounded-md border border-[#1e3d3a]/15 bg-white px-2 py-1 text-[11px] text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none"
                  >
                    {TIMEZONES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : (
                  <span className="font-semibold text-[#1e3d3a]">{bookingTz(selected)}</span>
                )}
                {!selected.tz && <span className="italic text-[#1e3d3a]/40">(assumed — set it above)</span>}
              </div>
              <div className="flex items-center gap-4 flex-wrap text-xs text-[#1e3d3a]/70">
                <span className="inline-flex items-center gap-1.5"><Mail className="h-3 w-3 text-[#a3823f]" strokeWidth={1.7} />{selected.email}</span>
                {selected.company && <span className="inline-flex items-center gap-1.5"><Building2 className="h-3 w-3 text-[#a3823f]" strokeWidth={1.7} />{selected.company}</span>}
                {selected.goal && <span className="inline-flex items-center gap-1.5"><Target className="h-3 w-3 text-[#a3823f]" strokeWidth={1.7} />{selected.goal}</span>}
              </div>
              {(selected.meet_url || selected.zoom_url) ? (
                <div className="flex items-center gap-2 flex-wrap pt-1" data-testid="calendar-connection">
                  <a
                    data-testid={`calendar-meet-link-${selected.id}`}
                    href={selected.meet_url || selected.zoom_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-3.5 py-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300"
                  >
                    <Video className="h-3.5 w-3.5" strokeWidth={1.8} />
                    Join Google Meet
                  </a>
                  <button
                    type="button"
                    data-testid={`calendar-copy-link-${selected.id}`}
                    onClick={() => { navigator.clipboard?.writeText(selected.meet_url || selected.zoom_url); }}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/20 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-[#1e3d3a]/70 hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300"
                  >
                    <Link2 className="h-3.5 w-3.5" strokeWidth={1.8} /> Copy link
                  </button>
                </div>
              ) : (
                onEnsureMeet && (
                  <button
                    type="button"
                    data-testid={`calendar-generate-meet-${selected.id}`}
                    disabled={genning}
                    onClick={async () => { setGenning(true); try { await onEnsureMeet(selected); } finally { setGenning(false); } }}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/25 px-3.5 py-1.5 text-[10px] uppercase tracking-[0.18em] text-[#1e3d3a]/75 hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300 disabled:opacity-50"
                  >
                    {genning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Video className="h-3.5 w-3.5" strokeWidth={1.8} />}
                    Generate Google Meet link
                  </button>
                )
              )}

              {/* Participants */}
              {onAddParticipant && (
                <div className="pt-2" data-testid="calendar-participants">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#1e3d3a]/55 mb-1.5">
                    <UserPlus className="h-3 w-3 text-[#a3823f]" strokeWidth={1.8} /> Participants
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                    <span className="inline-flex items-center rounded-full bg-[#1e3d3a]/8 px-2.5 py-1 text-[11px] text-[#1e3d3a]/70">{selected.email} <span className="ml-1 text-[9px] uppercase text-[#a3823f]">client</span></span>
                    {(selected.participants || []).map((pEmail) => (
                      <span key={pEmail} className="inline-flex items-center gap-1 rounded-full bg-[#c9a961]/15 px-2.5 py-1 text-[11px] text-[#1e3d3a]/80" data-testid={`participant-chip-${pEmail}`}>
                        {pEmail}
                        {onRemoveParticipant && (
                          <button type="button" title="Remove" onClick={() => onRemoveParticipant(selected, pEmail)} className="text-[#1e3d3a]/40 hover:text-[#b23a48]"><X className="h-3 w-3" /></button>
                        )}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <input
                      data-testid="participant-email-input"
                      type="email"
                      value={partEmail}
                      onChange={(e) => setPartEmail(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); document.querySelector('[data-testid="participant-add-btn"]')?.click(); } }}
                      placeholder="add-guest@email.com"
                      className="flex-1 min-w-[180px] rounded-md border border-[#1e3d3a]/15 bg-white px-2.5 py-1.5 text-[12px] text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none"
                    />
                    <button
                      type="button"
                      data-testid="participant-add-btn"
                      disabled={adding || !partEmail.trim()}
                      onClick={async () => {
                        const em = partEmail.trim();
                        if (!em) return;
                        setAdding(true);
                        try { await onAddParticipant(selected, em); setPartEmail(""); } catch { /* toast handled */ } finally { setAdding(false); }
                      }}
                      className="inline-flex items-center gap-1 rounded-md bg-[#1e3d3a] px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-[#12302c] transition-colors duration-300 disabled:opacity-50"
                    >
                      {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add
                    </button>
                  </div>
                  <p className="text-[10px] text-[#1e3d3a]/40 mt-1">They'll get a Google Calendar invite with the Meet link.</p>
                </div>
              )}
            </div>
            <div className="flex items-center gap-4">
              <label className="inline-flex items-center gap-1.5 cursor-pointer text-[10px] uppercase tracking-[0.18em] font-semibold text-[#4d5a24]">
                <input
                  type="checkbox"
                  data-testid={`calendar-showed-${selected.id}`}
                  checked={selected.attendance === "showed"}
                  onChange={() => onSetAttendance(selected, "showed")}
                  className="h-4 w-4 accent-[#6b7c3a] cursor-pointer"
                />
                Showed
              </label>
              <label className="inline-flex items-center gap-1.5 cursor-pointer text-[10px] uppercase tracking-[0.18em] font-semibold text-[#b23a48]">
                <input
                  type="checkbox"
                  data-testid={`calendar-noshow-${selected.id}`}
                  checked={selected.attendance === "no_show"}
                  onChange={() => onSetAttendance(selected, "no_show")}
                  className="h-4 w-4 accent-[#b23a48] cursor-pointer"
                />
                No-show
              </label>
              <button
                type="button"
                data-testid="calendar-detail-close"
                onClick={() => setSelectedId(null)}
                className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[#1e3d3a]/40 hover:text-[#1e3d3a] hover:bg-[#1e3d3a]/8 transition-colors duration-300"
              >
                <X className="h-4 w-4" strokeWidth={1.7} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
